import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import getUserRoles from '../../api/graphql/queries/getUserRoles';

const RolesTagInput = ({ roles, onChange, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchRoles = async () => {
      const fetched = await getUserRoles();
      setAllRoles(fetched);
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = allRoles.filter(
        r => r.toLowerCase().includes(inputValue.toLowerCase()) && !roles.includes(r.toLowerCase()),
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [inputValue, allRoles, roles]);

  const addRole = (role) => {
    const normalized = role.toLowerCase().trim();
    if (normalized && !roles.includes(normalized)) {
      onChange([...roles, normalized]);
    }
    setInputValue('');
    setShowSuggestions(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const removeRole = (role) => {
    onChange(roles.filter(r => r !== role));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addRole(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && roles.length > 0) {
      removeRole(roles[roles.length - 1]);
    }
  };

  return (
    <div ref={containerRef} className="roles-tag">
      <div className="roles-tag__label">Roles</div>
      <div
        className="roles-tag__field"
        onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
      >
        {roles.map(role => (
          <span
            key={role}
            className={`roles-tag__chip${disabled ? ' roles-tag__chip--static' : ''}`}
          >
            {role}
            {!disabled && (
              <button
                type="button"
                className="roles-tag__chip-delete"
                aria-label={`Remove ${role}`}
                onClick={() => removeRole(role)}
              >
                &times;
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            className="roles-tag__input"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => { if (inputValue.trim()) setShowSuggestions(true); }}
            onKeyDown={handleKeyDown}
            placeholder={roles.length === 0 ? 'Type to add a role...' : ''}
          />
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="roles-tag__suggestions">
          {suggestions.map(s => (
            <div
              key={s}
              className="roles-tag__suggestion"
              onMouseDown={(e) => { e.preventDefault(); addRole(s); }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

RolesTagInput.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

RolesTagInput.defaultProps = {
  roles: [],
  disabled: false,
};

export default RolesTagInput;
